import { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { phoneHref } from "utils/formatPhone";

/**
 * Links inside the tenant's rich text (FAQ answers, policy pages) after sanitizeHtml.
 *
 * The package's sanitiser marks every link target="_blank" rel="noopener noreferrer", on the
 * footing that tenant copy points off-site. Three kinds of link are not off-site and should
 * not open a tab of their own: a page of this site ("/terms-and-conditions"), a tel: link
 * (a blank tab stays behind once the dialler opens) and a mailto: link.
 */
const STAYS = /^(\/(?!\/)|tel:|mailto:)/i;

/** The sanitised html with target and rel taken off the links that belong in the same tab. */
export const sameTabLinks = (html: string): string =>
  html.replace(/<a\s[^>]*>/gi, (tag) => {
    const href = /\shref="([^"]*)"/i.exec(tag)?.[1] ?? "";
    return STAYS.test(href) ? tag.replace(/\s(?:target|rel)="[^"]*"/gi, "") : tag;
  });

/**
 * onClick for a box of rich text: a click on a link to a page of this site goes there inside
 * the app, so the renter keeps the session and the page does not reload. Anything else, a new
 * tab or a modified click included, is left to the browser.
 */
export const useRichLinkClick = () => {
  const navigate = useNavigate();
  return (e: MouseEvent<HTMLElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const link = (e.target as HTMLElement).closest?.("a");
    const href = link?.getAttribute("href") ?? "";
    if (!link || link.getAttribute("target") === "_blank" || !/^\/(?!\/)/.test(href)) return;
    e.preventDefault();
    navigate(href);
  };
};

// An address, an e-mail or a phone number in running text. A phone is judged by its digits below.
const TOKEN = /(https?:\/\/[^\s<>"']+)|([\w.+-]+@[\w-]+(?:\.[\w-]+)+)|(\+?\d[\d\s().-]{7,}\d)/g;
const END_PUNCT = /[.,;:!?)\]]+$/;

/** The link a piece of text is, or null: the part that is the link, the href and what follows it. */
const linkOf = (m: RegExpExecArray): { text: string; href: string; tail: string } | null => {
  if (m[1]) {
    const text = m[1].replace(END_PUNCT, "");
    return { text, href: text, tail: m[1].slice(text.length) };
  }
  if (m[2]) return { text: m[2], href: `mailto:${m[2]}`, tail: "" };
  const digits = m[3].replace(/\D/g, "");
  // 10 to 15 digits: a phone number, not a date or a price.
  if (digits.length < 10 || digits.length > 15) return null;
  // A number written with its leading 0 is a national one: dialled as typed, with no "+" put in front of it.
  return { text: m[3], href: /^0/.test(m[3]) ? `tel:${digits}` : phoneHref(m[3]), tail: "" };
};

/**
 * An operator's insurance note (Quill html after sanitizeHtml) with its web addresses, e-mails and phone
 * numbers made links (1NOW-11204): the editor saves them as plain text unless the operator pasted a link.
 * Links are built node by node from the matched text, never by gluing strings into markup. A link whose
 * words hold a different web address ("Please visit https://x" wrapped in a mailto: link) is taken off,
 * so the address in it gets its own link instead of the wrong one around the whole sentence.
 */
export const linkifyHtml = (html: string): string => {
  if (!html || typeof document === "undefined") return html;
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  tpl.content.querySelectorAll("a").forEach((a) => {
    const url = /https?:\/\/[^\s<>"']+/.exec(a.textContent ?? "")?.[0].replace(END_PUNCT, "");
    if (url && !(a.getAttribute("href") ?? "").includes(url)) a.replaceWith(...Array.from(a.childNodes));
  });
  const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  while (walker.nextNode()) texts.push(walker.currentNode as Text);
  texts.forEach((node) => {
    if (node.parentElement?.closest("a")) return;
    const text = node.data;
    const out = document.createDocumentFragment();
    let at = 0;
    for (const m of Array.from(text.matchAll(TOKEN))) {
      const link = linkOf(m as RegExpExecArray);
      if (!link?.href) continue;
      const start = m.index ?? 0;
      out.append(text.slice(at, start));
      const a = document.createElement("a");
      a.href = link.href;
      a.textContent = link.text;
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
      out.append(a, link.tail);
      at = start + link.text.length + link.tail.length;
    }
    if (at === 0) return;
    out.append(text.slice(at));
    node.replaceWith(out);
  });
  // Mail and phone links open the mail app and the dialler, so they keep no tab of their own.
  return sameTabLinks(tpl.innerHTML);
};

/** A plain-text note as html with its links made (see linkifyHtml); the text is escaped first. */
export const linkifyText = (text: string): string => {
  const el = document.createElement("div");
  el.textContent = text;
  return linkifyHtml(el.innerHTML);
};
